import { asOptionalRecord } from "@openclaw/normalization-core/record-coerce";

const PRESENTATION_GUARD_KEY = "openclaw.controlUi.presentationGuard.v1";
const MAX_STATE_LENGTH = 256;

type PresentationGuardRecord = {
  pluginId: string;
  gatewayUrl: string;
  profileId: string;
  state: string;
  generation: string;
};

type PresentationGuardBinding = Pick<
  PresentationGuardRecord,
  "pluginId" | "gatewayUrl" | "profileId"
>;

function readRecord(): PresentationGuardRecord | null {
  try {
    const raw = sessionStorage.getItem(PRESENTATION_GUARD_KEY);
    if (!raw) return null;
    const value = asOptionalRecord(JSON.parse(raw));
    if (!value || Object.keys(value).length !== 5) return null;
    const { pluginId, gatewayUrl, profileId, state, generation } = value;
    const bounded = (field: unknown, max: number): field is string =>
      typeof field === "string" && field.length > 0 && field.length <= max;
    if (
      !bounded(pluginId, 128) ||
      !bounded(gatewayUrl, 2048) ||
      !bounded(profileId, 256) ||
      !bounded(state, MAX_STATE_LENGTH) ||
      !bounded(generation, 128)
    )
      return null;
    return { pluginId, gatewayUrl, profileId, state, generation };
  } catch {
    return null;
  }
}

function sameBinding(record: PresentationGuardRecord, binding: PresentationGuardBinding): boolean {
  return (
    record.pluginId === binding.pluginId &&
    record.gatewayUrl === binding.gatewayUrl &&
    record.profileId === binding.profileId
  );
}

class PresentationGuardBoot {
  #cover: HTMLDivElement | null = null;
  #record: PresentationGuardRecord | null;
  #visible = false;

  constructor() {
    this.#record = readRecord();
    if (this.#record || this.hasStoredValue()) this.show();
  }

  hasStoredValue(): boolean {
    try {
      return sessionStorage.getItem(PRESENTATION_GUARD_KEY) !== null;
    } catch {
      return false;
    }
  }

  current(): PresentationGuardRecord | null {
    const record = readRecord();
    this.#record = record;
    return record;
  }

  show(): void {
    if (this.#visible) return;
    this.#visible = true;
    document.documentElement.style.visibility = "hidden";
    const mount = () => {
      if (!this.#visible || this.#cover) return;
      const cover = document.createElement("div");
      cover.setAttribute("role", "alertdialog");
      cover.setAttribute("aria-modal", "true");
      cover.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:grid;place-items:center;padding:24px;background:var(--bg);color:var(--text);font:16px system-ui,sans-serif;visibility:visible";
      const panel = document.createElement("div");
      panel.style.cssText = "max-width:32rem;text-align:center";
      const title = document.createElement("h1");
      title.textContent = "Presentation override is restoring";
      const message = document.createElement("p");
      message.textContent = "Personal presentation stays hidden until its owner is ready.";
      const retry = document.createElement("button");
      retry.type = "button";
      retry.textContent = "Retry";
      retry.onclick = () => window.location.reload();
      const disable = document.createElement("button");
      disable.type = "button";
      disable.textContent = "Turn off override";
      disable.style.marginInlineStart = "12px";
      disable.onclick = () => {
        try {
          sessionStorage.removeItem(PRESENTATION_GUARD_KEY);
        } catch {
          /* keep the visible recovery action */
        }
        window.location.reload();
      };
      panel.append(title, message, retry, disable);
      cover.append(panel);
      document.body.append(cover);
      this.#cover = cover;
    };
    if (document.body) mount();
    else document.addEventListener("DOMContentLoaded", mount, { once: true });
  }

  hide(): void {
    this.#visible = false;
    this.#cover?.remove();
    this.#cover = null;
    document.documentElement.style.visibility = "";
  }

  activate(binding: PresentationGuardBinding, state: string): PresentationGuardRecord {
    if (state.length === 0 || state.length > MAX_STATE_LENGTH)
      throw new Error("Presentation guard state must be 1 to 256 characters.");
    const current = this.current();
    if (current && !sameBinding(current, binding))
      throw new Error("Another plugin owns the active presentation override in this window.");
    const record: PresentationGuardRecord = {
      ...binding,
      state,
      generation: crypto.randomUUID(),
    };
    try {
      sessionStorage.setItem(PRESENTATION_GUARD_KEY, JSON.stringify(record));
    } catch (error) {
      throw new Error(`Could not persist presentation override: ${String(error)}`);
    }
    this.#record = record;
    this.show();
    return record;
  }

  disable(binding: PresentationGuardBinding, generation: string): void {
    const current = this.current();
    if (!current || !sameBinding(current, binding) || current.generation !== generation)
      throw new Error("Presentation override ownership has changed.");
    try {
      sessionStorage.removeItem(PRESENTATION_GUARD_KEY);
    } catch (error) {
      throw new Error(`Could not disable presentation override: ${String(error)}`);
    }
    this.#record = null;
    this.hide();
  }

  valid(binding: PresentationGuardBinding, generation: string): boolean {
    const current = this.current();
    return current !== null && sameBinding(current, binding) && current.generation === generation;
  }
}

/** Installed before app-host imports so a persisted override covers first paint. */
export const presentationGuardBoot = new PresentationGuardBoot();
export type { PresentationGuardBinding, PresentationGuardRecord };
